/**
 * Browser-side crash reporting for the rollout.
 *
 * The server's logs only ever contain what the server did. A React render that
 * throws, a chunk that fails to load, an MSAL popup blocked by the browser —
 * none of it reaches Log Analytics, and none of it is something the person who
 * hit it can usefully describe. This ships those to /api/dev/client-events so
 * they can be read back on /dev.
 *
 * Three constraints shape everything here:
 *
 *   1. Reporting must never throw. It runs inside error handlers; an exception
 *      raised while reporting an exception is how a broken page becomes an
 *      unrecoverable one.
 *   2. It must be bounded. A component that throws on every frame would
 *      otherwise generate requests until the tab dies.
 *   3. It must not report its own failures, or a network outage becomes an
 *      infinite loop.
 */

interface QueuedReport {
  message: string;
  stack?: string;
  url: string;
  level: 'error' | 'warn';
  context?: Record<string, unknown>;
}

const ENDPOINT = '/api/dev/client-events';

/**
 * Hard ceiling per page load. A render loop can produce thousands of identical
 * errors a second; past this point the console has long since got the point,
 * and continuing only makes the page slower to recover.
 */
const MAX_REPORTS_PER_LOAD = 50;
const FLUSH_DELAY_MS = 2_000;
const DEDUPE_WINDOW_MS = 10_000;

let queue: QueuedReport[] = [];
let sent = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

/** message+stack → last time it was queued, so a loop reports once, not always. */
const recentlySeen = new Map<string, number>();

function seenTooRecently(key: string): boolean {
  const now = Date.now();

  // Opportunistic sweep — this map would otherwise grow for the life of the tab.
  for (const [seenKey, at] of recentlySeen) {
    if (now - at > DEDUPE_WINDOW_MS) recentlySeen.delete(seenKey);
  }

  const last = recentlySeen.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;

  recentlySeen.set(key, now);
  return false;
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (queue.length === 0) return;

  const batch = queue;
  queue = [];

  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      // The reports are useful but never worth blocking a navigation for.
      keepalive: true,
    });
  } catch {
    // Swallowed on purpose: retrying a failed report is how a network blip
    // turns into a loop, and these are diagnostics rather than data.
  }
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
}

/**
 * Queues one error. Safe to call from anywhere, including error handlers —
 * it catches its own failures and returns.
 */
export function reportError(
  error: unknown,
  context?: Record<string, unknown>,
  level: 'error' | 'warn' = 'error',
): void {
  try {
    if (sent >= MAX_REPORTS_PER_LOAD) return;

    const asError = error instanceof Error ? error : null;
    const message = asError
      ? `${asError.name}: ${asError.message}`
      : typeof error === 'string'
        ? error
        : safeStringify(error);

    if (!message) return;

    const stack = asError?.stack;
    if (seenTooRecently(`${message} ${stack ?? ''}`)) return;

    sent += 1;
    queue.push({
      message,
      ...(stack ? { stack } : {}),
      url: window.location.pathname,
      level,
      ...(context ? { context } : {}),
    });
    scheduleFlush();
  } catch {
    // Reporting is best-effort by definition.
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)?.slice(0, 2_000) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Attaches the global handlers. Called once, before the app mounts, so a crash
 * during the first render is captured too.
 */
export function installErrorReporting(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Failed <img>/<script> loads arrive here with no error object; they are
    // noise unless the page is genuinely broken, and the message would be the
    // useless "Script error." string.
    if (!event.error && !event.message) return;
    reportError(event.error ?? event.message, {
      kind: 'window.onerror',
      ...(event.filename ? { filename: event.filename, line: event.lineno } : {}),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportError(event.reason, { kind: 'unhandledrejection' });
  });

  // A queued batch would otherwise be lost when the tab closes — which is
  // exactly what someone does after hitting a bug.
  window.addEventListener('pagehide', () => {
    if (queue.length === 0) return;
    try {
      const body = JSON.stringify({ events: queue });
      queue = [];
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } catch {
      // Nothing further to try at this point in the page's life.
    }
  });
}
